import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Loader2, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "../lib/api";

export function TwoFactorModal({ open, onClose }) {
  const [status, setStatus] = useState(null); // {enabled}
  const [step, setStep] = useState("idle"); // idle | code
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setStep("idle"); setCode(""); setChallenge(null); api.twofaStatus().then(setStatus).catch(() => setStatus({ enabled: false })); }
  }, [open]);

  if (!open) return null;

  const startEnable = async () => {
    setBusy(true);
    try {
      const d = await api.twofaEnableStart();
      setChallenge(d); setStep("code");
      toast.info("Code envoyé", { description: `Consultez votre courriel ${d.email_masked}` });
    } catch (e) { toast.error("Échec", { description: e?.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const d = await api.twofaEnableConfirm({ challenge_id: challenge.challenge_id, code: code.trim() });
      if (d?.token) setToken(d.token);
      setStatus({ enabled: true }); setStep("idle"); setCode("");
      toast.success("Double authentification activée");
    } catch (err) { toast.error("Code invalide", { description: err?.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Désactiver la double authentification ?")) return;
    setBusy(true);
    try { const d = await api.twofaDisable(); if (d?.token) setToken(d.token); setStatus({ enabled: false }); toast.success("Double authentification désactivée"); }
    catch (e) { toast.error("Échec"); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} data-testid="twofa-modal">
      <div onClick={(e) => e.stopPropagation()} className="card gold-border" style={{ width: 420, maxWidth: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Sécurité du compte</h3>
          <button className="top-btn" onClick={onClose} data-testid="twofa-modal-close"><X size={18} /></button>
        </div>

        {!status ? <div style={{ textAlign: "center", padding: 20 }}><Loader2 className="spin" /></div> : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <div className="mc-ico">{status.enabled ? <ShieldCheck size={22} /> : <ShieldOff size={22} />}</div>
              <div>
                <div style={{ fontWeight: 700 }}>Double authentification (2FA)</div>
                <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
                  {status.enabled ? "Activée — un code par courriel est requis à chaque connexion." : "Désactivée — protégez votre compte avec un code par courriel."}
                </div>
              </div>
              <span className={`badge ${status.enabled ? "badge-green" : "badge-muted"}`} style={{ marginLeft: "auto" }} data-testid="twofa-status-badge">
                {status.enabled ? "Activée" : "Désactivée"}
              </span>
            </div>

            {step === "code" ? (
              <form onSubmit={confirmEnable}>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 12px", display: "flex", gap: 7, alignItems: "center" }}>
                  <Mail size={14} /> Code envoyé à {challenge?.email_masked}
                </p>
                <input className="input" style={{ width: "100%", marginBottom: 14, textAlign: "center", letterSpacing: 8, fontSize: 22, fontFamily: "monospace" }}
                  inputMode="numeric" maxLength={6} value={code} autoFocus
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" data-testid="twofa-enable-code" />
                <button className="btn btn-gold" style={{ width: "100%" }} disabled={busy || code.length < 6} data-testid="twofa-enable-confirm">
                  {busy ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />} Confirmer l'activation
                </button>
              </form>
            ) : status.enabled ? (
              <button className="btn btn-secondary" style={{ width: "100%" }} disabled={busy} onClick={disable} data-testid="twofa-disable-btn">
                {busy ? <Loader2 size={16} className="spin" /> : <ShieldOff size={16} />} Désactiver la 2FA
              </button>
            ) : (
              <button className="btn btn-gold" style={{ width: "100%" }} disabled={busy} onClick={startEnable} data-testid="twofa-enable-btn">
                {busy ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />} Activer la 2FA
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
